/**
 * TerrainColorMapPicker — color ramp selector for terrain elevation.
 *
 * Renders preset ramp chips that the user can tap to change the
 * elevation-to-color mapping on the terrain layer.
 */

import { Chip, tokens, type IconName } from '@/design';
import type { TerrainToolCardState } from '@/state/useViewerStore';

type ColorRampPreset = TerrainToolCardState['colorRamp'];

export interface TerrainColorMapPickerProps {
  activeRamp: ColorRampPreset;
  onChange: (ramp: ColorRampPreset) => void;
  'data-testid'?: string;
}

const RAMP_OPTIONS: { id: ColorRampPreset; label: string; icon: IconName }[] = [
  { id: 'hypsometric', label: 'Hypsometric', icon: 'terrain' },
  { id: 'plasma', label: 'Plasma', icon: 'gradient' },
  { id: 'rdylgn', label: 'RdYlGn', icon: 'palette' },
  { id: 'gray', label: 'Grayscale', icon: 'contrast' },
  { id: 'none', label: 'None', icon: 'block' },
];

export function TerrainColorMapPicker({
  activeRamp,
  onChange,
  'data-testid': testId,
}: TerrainColorMapPickerProps) {
  return (
    <div
      role="radiogroup"
      aria-label="Elevation color ramp"
      data-testid={testId ?? 'terrain-colormap-picker'}
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: tokens.space[1],
      }}
    >
      {RAMP_OPTIONS.map((opt) => (
        <Chip
          key={opt.id}
          icon={opt.icon}
          variant={activeRamp === opt.id ? 'info' : 'neutral'}
          label={opt.label}
          pressable
          pressed={activeRamp === opt.id}
          onClick={() => onChange(opt.id)}
          data-testid={`terrain-ramp-${opt.id}`}
        />
      ))}
    </div>
  );
}
